import * as React from 'react';
const { useState, useEffect, useCallback } = React;
import { bindActionCreators } from 'redux';
import api from '@seeneco-ui/api';
import Drawer from '@seeneco-ui/core/components/drawer';

import { withAutoUpdate, IBusinessState } from '../../../../../flux';
import { NON_UPDATED_PASSWORD } from '../../../../../shared/constants/specific';
import { integrationsActionsPack, AuthorizeExternalSystemAccountOptions } from '../../../actions';
import { integrationsSelectorsPack } from '../../../selectors';
import { counterpartyActionsPack } from '../../../../counterparty/actions';
import { counterpartySelectorsPack } from '../../../../counterparty/selectors';
import XUpdateExternalSystemAccountForm from './update-external-system-account.view';


const addExternalSystemAccountAsPromise = integrationsActionsPack.actions.addExternalSystemAccount as (account: ExternalSystemAccount) => Promise<ExternalSystemAccount>;

type SharedProps = {
	type: 'ADD' | 'EDIT';
	cms: CashManagementSystem;
	accountID?: number;
}

type ConfigDrawer = {} & SharedProps

type UpdateExternalSystemAccountFormProps = {
	isFetching?: boolean;
	resMap?: Record<string, RemoteExternalService>;
	ssiMap?: Record<string, SubsystemInstance>;
	teList?: Array<CounterpartyBrief>;
	fetchTenantEntities?: () => void;
	addExternalSystemAccount?: (account: ExternalSystemAccount) => void;
	changeExternalSystemAccount?: (account: ExternalSystemAccount) => void;
	authorizeExternalSystemAccount?: (options: AuthorizeExternalSystemAccountOptions) => void;
} & SharedProps;

const UpdateExternalSystemAccountForm: React.SFC<UpdateExternalSystemAccountFormProps> = (props) => {
	const {
		accountID,
		cms,
		resMap,
		ssiMap,
		teList,
		addExternalSystemAccount,
		changeExternalSystemAccount,
		authorizeExternalSystemAccount,
		fetchTenantEntities,
		...rest
	} = props;
	const [isFetching, setIsFetching] = useState(true);
	const [account, setAccount] = useState(null);
	const isFetchingShared = props.isFetching || isFetching;

	useEffect(() => {
		const { type, accountID } = props;

		if (type === 'ADD') {
			setAccount(new api.platform.security.package.ExternalSystemAccount());
			setIsFetching(false);
		} else if (type === 'EDIT') {
			api.platform.security.externalSystemService.fetchExternalSystemAccountByID(accountID).then((account: ExternalSystemAccount) => {
				account.AdminPassword = NON_UPDATED_PASSWORD;
				setAccount(account);
				setIsFetching(false);
			});
		}
	}, []);

	const handleSetFormHeader = useCallback((header: React.ReactNode) => {
		Drawer.default.get().setContent({
			header: <Drawer.default.Header>{header}</Drawer.default.Header>
		})
	}, []);
	const handleSetFormControls = useCallback((controls: React.ReactNode) => {
		Drawer.default.get().setContent({
			footer: <Drawer.default.Footer>{controls}</Drawer.default.Footer>
		})
	}, []);
	const handleClose = useCallback(() => Drawer.default.get().close(), []);

	return (
		<XUpdateExternalSystemAccountForm
			{...rest}
			isFetching={isFetchingShared}
			account={account}
			cms={cms}
			resMap={resMap}
			ssiMap={ssiMap}
			teList={teList}
			onSetFormHeader={handleSetFormHeader}
			onSetFormControls={handleSetFormControls}
			onClose={handleClose}
			addExternalSystemAccount={addExternalSystemAccount}
			addExternalSystemAccountAsPromise={addExternalSystemAccountAsPromise}
			changeExternalSystemAccount={changeExternalSystemAccount}
			authorizeExternalSystemAccount={authorizeExternalSystemAccount}
		/>
	);
}

const selectIsFetching = state => integrationsSelectorsPack.selectAsyncExternalSubsystemInstances.selectIsFetching(state)
	|| integrationsSelectorsPack.selectAsyncRemoteExternalServices.selectIsFetching(state)
	|| counterpartySelectorsPack.selectAsyncTenantEntities.selectIsFetching(state);

function mapStateToProps(state: IBusinessState): Partial<UpdateExternalSystemAccountFormProps> {
	return {
		isFetching: selectIsFetching(state),
		ssiMap: integrationsSelectorsPack.selectAsyncExternalSubsystemInstances.selectItem(state),
		resMap: integrationsSelectorsPack.selectAsyncRemoteExternalServices.selectItem(state),
		teList: counterpartySelectorsPack.selectTenantEntitiesList(state),
	};
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({
		addExternalSystemAccount: integrationsActionsPack.actions.addExternalSystemAccount,
		changeExternalSystemAccount: integrationsActionsPack.actions.changeExternalSystemAccount,
		authorizeExternalSystemAccount: integrationsActionsPack.actions.authorizeExternalSystemAccount,
		fetchTenantEntities: counterpartyActionsPack.actions.fetchTenantEntities,
	}, dispatch);
}

const EnhancedUpdateExternalSystemAccountForm = withAutoUpdate<IBusinessState, UpdateExternalSystemAccountFormProps>(mapStateToProps, mapDispatchToProps)({
	teDidInvalidate: [
		counterpartySelectorsPack.selectAsyncTenantEntities.selectDidInvalidate,
		({ fetchTenantEntities }) => fetchTenantEntities(),
	]
})(UpdateExternalSystemAccountForm);

function openUpdateExternalSystemAccountFormDrawer(config: ConfigDrawer) {
	const {
		type,
		accountID,
		cms,
	} = config;

	Drawer.default.get().open({
		content:
			<Drawer.default.Content>
				<EnhancedUpdateExternalSystemAccountForm
					type={type}
					accountID={accountID}
					cms={cms}
				/>
			</Drawer.default.Content>
	});
}

export {
	UpdateExternalSystemAccountForm,
	openUpdateExternalSystemAccountFormDrawer
};
export default UpdateExternalSystemAccountForm;
